'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { useNavigationProgressStore } from '@/store/navigationProgress';

/** Pending time before the bar is allowed to appear — prefetched navigations finish inside this. */
export const APPEAR_DELAY_MS = 120;
/** Gap between ramp increments; also the bar's CSS transition, so ticks read as one glide. */
export const TICK_MS = 250;
/** Where the bar starts once it becomes visible. */
export const INITIAL_VALUE = 20;
/** The ramp never passes this on its own — only the commit takes it to 100. */
export const CEILING = 90;
/** How long 100% stays on screen while it fades out. */
export const FADE_MS = 220;
/** Hard stop, so an aborted or blocked navigation can never leave the bar running. */
export const SAFETY_TIMEOUT_MS = 8000;

const PHASE = { IDLE: 'idle', RUNNING: 'running', FINISHING: 'finishing' };

// Decelerating ramp, same shape as nprogress: big jumps early, a crawl near the ceiling.
// A lookup table instead of chained ternaries keeps this readable (and Sonar S3358 quiet).
const RAMP = [
	{ below: 40, step: 12 },
	{ below: 65, step: 6 },
	{ below: 80, step: 3 },
	{ below: CEILING, step: 1 }
];

const rampFrom = value => {
	const bracket = RAMP.find(entry => value < entry.below);
	if (!bracket) return value;
	return Math.min(value + bracket.step, CEILING);
};

/**
 * Drives the global top loader.
 *
 * START comes from `onRouterTransitionStart` (src/instrumentation-client.js) writing to the store —
 * the only place the App Router reports the *start* of a client navigation, and it covers <Link>,
 * router.push/replace and browser back/forward alike.
 *
 * STOP is the committed pathname: `usePathname` updates only after the router commits, which is
 * exactly when the new route is on screen. A safety timeout covers navigations that never commit.
 *
 * `useSearchParams` is deliberately not used here — calling it from a component in the root layout
 * would bail the whole app out to client-side rendering. Query-only navigations are skipped by the
 * store anyway.
 */
export const useNavigationProgress = () => {
	const pathname = usePathname();
	const isNavigating = useNavigationProgressStore(state => state.isNavigating);
	const settle = useNavigationProgressStore(state => state.settle);
	const abort = useNavigationProgressStore(state => state.abort);

	const [value, setValue] = useState(0);
	const [phase, setPhase] = useState(PHASE.IDLE);
	// Written only from effects, never during render, so react-hooks/refs stays quiet.
	const runningRef = useRef(false);

	// Seeds committedPath on mount, then ends every subsequent navigation.
	useEffect(() => {
		settle(pathname);
	}, [pathname, settle]);

	useEffect(() => {
		if (!isNavigating) return undefined;

		const appear = setTimeout(() => {
			runningRef.current = true;
			setValue(INITIAL_VALUE);
			setPhase(PHASE.RUNNING);
		}, APPEAR_DELAY_MS);
		const safety = setTimeout(abort, SAFETY_TIMEOUT_MS);

		return () => {
			clearTimeout(appear);
			clearTimeout(safety);
		};
	}, [isNavigating, abort]);

	useEffect(() => {
		if (phase !== PHASE.RUNNING) return undefined;
		const ticker = setInterval(() => setValue(rampFrom), TICK_MS);
		return () => clearInterval(ticker);
	}, [phase]);

	useEffect(() => {
		// runningRef is false when the navigation resolved before APPEAR_DELAY_MS — the fast,
		// prefetched case, where the bar must never have flashed at all.
		if (isNavigating || !runningRef.current) return undefined;
		runningRef.current = false;
		setValue(100);
		setPhase(PHASE.FINISHING);
		const fade = setTimeout(() => setPhase(PHASE.IDLE), FADE_MS);
		return () => clearTimeout(fade);
	}, [isNavigating]);

	return { value, visible: phase !== PHASE.IDLE, finishing: phase === PHASE.FINISHING };
};
